import { throwError } from 'rxjs';
import { AppConfig } from '../../core/app.config';
import { BACKGROUND_NETWORK_REQ_OPTIONS, BACKGROUND_NETWORK_REQ_UI_OPTIONS } from '../../core/constants/constants';
import { NetworkRequestOptions } from '../../core/interfaces/request';
import { courier } from '../../core/services/courier.service';
import { createHttpParams } from '../../core/utils/request';
import { buildBillingDetailsParams } from './billing-details/BillingDetailsForm/form-helper';
import { BillingProvider } from './models/payment';

const appConfig = new AppConfig();
let params: any;

const PaymentAPI = {
  plansUrl: `${appConfig.getIronBankURL()}/plans`,
  workflowsURL: appConfig.getWorkflowsURL(),
  paymentURL: appConfig.getIronBankURL(),
  integrationsURL: appConfig.getIntegrationsURL(),
  usageURL: `${appConfig.getIronBankURL()}/usage`,
  h20UsageURL: `${appConfig.getIronBankURL()}/h20/usage`,
  saveCustomPlanURL: `${appConfig.getIronBankURL()}/custom-plan`,
  savePlanURL: `${appConfig.getIronBankURL()}/subscriptions`,
  onDemandEventsURL: `${appConfig.getIronBankURL()}/on-demand-events`,
  multiRegionURL: `${appConfig.getSecurityServiceURL()}/multi-region`,
  multiRegionUsageUrl: `${appConfig.getSecurityServiceURL()}/multi-region/usage`,
  h20MultiRegionUsageURL: `${appConfig.getSecurityServiceURL()}/multi-region/h20/usage`,
  onDemandCreditURL: `${appConfig.getIronBankURL()}/on-demand-credit`,
  upcomingInvoiceURL: `${appConfig.getIronBankURL()}/upcoming-invoice`,
  trialPeriodUsageUrl: `${appConfig.getIronBankURL()}/trial-usage`,
  paidSourceUrl: `${appConfig.getIronBankURL()}/paid-source`,

  getInvoices(pageMeta) {
    const invoicesUrl = `${this.paymentURL}/invoices`;
    const options: NetworkRequestOptions = {
      ...BACKGROUND_NETWORK_REQ_OPTIONS,
      networkOptions: {
        params: createHttpParams({
          limit: pageMeta.limit,
          starting_after: pageMeta.startingAfter,
          ending_before: pageMeta.endingBefore
        })
      }
    };

    return courier.get(invoicesUrl, options);
  },

  getBillingDetails() {
    const billingUrl = `${this.paymentURL}/billing`;

    return courier.get(billingUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  editBillingDetails(data: any) {
    const billingUrl = `${this.paymentURL}/billing`;

    if (!data?.free_plan) {
      params = buildBillingDetailsParams(data);
    } else {
      params = data;
    }

    return courier.post(billingUrl, BACKGROUND_NETWORK_REQ_OPTIONS, params);
  },

  getCountries() {
    const requestUrl = `/geo/countries.json?timestamp=${new Date().getTime()}`;

    return courier.get(requestUrl, BACKGROUND_NETWORK_REQ_OPTIONS).then(countries =>
      Object.entries(countries).map(([key, value]) => ({
        name: key,
        code: value
      }))
    );
  },

  getCountryStatesMap() {
    const requestUrl = `/geo/country-states.json?timestamp=${new Date().getTime()}`;

    return courier.get(requestUrl, BACKGROUND_NETWORK_REQ_OPTIONS).then(states =>
      Object.keys(states).reduce(
        (dict, countryName) => ({
          ...dict,
          [countryName]: Object.keys(states[countryName]).map(stateName => ({
            name: stateName,
            code: states[countryName][stateName]
          }))
        }),
        {}
      )
    );
  },

  addCard(stripeAddCardToken: any, stripeTestCardToken: any) {
    const cardsUrl = `${this.paymentURL}/cards`;

    const params: any = {
      token_id: stripeAddCardToken,
      test_transaction_token_id: stripeTestCardToken
    };

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false,
        showSuccessMsg: false,
        showErrorMsg: false,
        showLoading: false
      }
    };

    return courier.post(cardsUrl, options, params);
  },

  getCards() {
    const cardsUrl = `${this.paymentURL}/cards`;

    return courier.get(cardsUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  makeDefault(id: any, networkOptions: any) {
    const requestUrl = `${this.paymentURL}/cards/${id}`;

    const options = {
      ...BACKGROUND_NETWORK_REQ_OPTIONS,
      ...networkOptions
    };

    return courier.put(requestUrl, options);
  },

  deleteCard(id: any) {
    const requestUrl = `${this.paymentURL}/cards/${id}`;

    const networkOptions = {
      uiOptions: {
        ...BACKGROUND_NETWORK_REQ_UI_OPTIONS,
        showErrorMsg: true
      }
    };

    return courier.delete(requestUrl, networkOptions);
  },

  getPipelinesUsageSummary() {
    const invoicesUrl = `${this.integrationsURL}/usage-summary`;

    return courier.get(invoicesUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getPipelinesUsageSummaryForH20() {
    const invoicesUrl = `${this.integrationsURL}/h20/usage-summary`;

    return courier.get(invoicesUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getMultiRegionPipelineUsageSummary() {
    const requestUrl = `${this.multiRegionURL}/usage-summary`;

    return courier.get(requestUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getMultiRegionUsageSummaryForH2O() {
    const requestUrl = `${this.multiRegionURL}/h20/usage-summary`;

    return courier.get(requestUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  downloadUsageSummary(params) {
    const requestUrl = `${this.integrationsURL}/usage-summary`;
    const options: NetworkRequestOptions = {
      uiOptions: {
        showErrorMsg: true,
        showLoading: false
      }
    };

    return courier.post(requestUrl, options, params);
  },

  getAvailablePlans() {
    const options: NetworkRequestOptions = {
      networkOptions: {
        params: createHttpParams({
          all_plans: true
        })
      },
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS
    };

    return courier.get(this.plansUrl, options);
  },

  getUsage(previousCycle = false) {
    const options: NetworkRequestOptions = {
      networkOptions: {
        params: createHttpParams({
          previous_cycle: previousCycle
        })
      },
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS
    };

    return courier.get(this.usageURL, options);
  },

  getH20Usage(previousCycle = false) {
    const options: NetworkRequestOptions = {
      networkOptions: {
        params: createHttpParams({
          previous_cycle: previousCycle
        })
      },
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS
    };

    return courier.get(this.h20UsageURL, options);
  },

  getH20MultiRegionUsage() {
    return courier.get(this.h20MultiRegionUsageURL, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getMultiRegionUsage() {
    return courier.get(this.multiRegionUsageUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getOnDemandCredit() {
    return courier.get(this.onDemandCreditURL, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getCurrentPlan() {
    const options: NetworkRequestOptions = {
      networkOptions: {
        params: createHttpParams({
          all_plans: false
        })
      },
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS
    };

    return courier.get(this.plansUrl, options);
  },

  payOnDemandUsedCreditBill() {
    return courier.post(`${this.onDemandCreditURL}/payment`, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  setOnDemandCreditLimit(events: number) {
    return courier.put(this.onDemandCreditURL, BACKGROUND_NETWORK_REQ_OPTIONS, {
      assigned_on_demand_credit_events: events
    });
  },

  contactSales(requirements: string, withMarketplaceProvider: string) {
    return courier.post(`${this.paymentURL}/business-plan`, BACKGROUND_NETWORK_REQ_OPTIONS, {
      requirement_text: requirements,
      marketplace_provider: withMarketplaceProvider || BillingProvider.STRIPE
    });
  },

  savePlan(id: string) {
    const body = {
      plan_id: id
    };

    return courier.post(this.savePlanURL, BACKGROUND_NETWORK_REQ_OPTIONS, body);
  },

  saveCustomPlan() {
    return courier.put(this.saveCustomPlanURL, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getOnDemandEvents() {
    return courier.get(this.onDemandEventsURL, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  getUpcomingInvoices() {
    return courier.get(this.upcomingInvoiceURL, BACKGROUND_NETWORK_REQ_OPTIONS).catch(err => {
      if (err && err.status === 404) {
        return { data: [] };
      }

      return throwError(err);
    });
  },

  getTrialPeriodUsage() {
    return courier.get(this.trialPeriodUsageUrl, BACKGROUND_NETWORK_REQ_OPTIONS);
  },

  paidSourceAttemptNotification(sourceType: string) {
    const requestParams = {
      source_type: sourceType
    };

    return courier.post(this.paidSourceUrl, BACKGROUND_NETWORK_REQ_OPTIONS, requestParams);
  }
};

export default PaymentAPI;
